import type { FicheEditHandlers } from '@/components/templates/FicheDessinee';
import type { FicheColor } from '@pharmafiche/schema';
import { useState } from 'react';
import { CardContextMenu } from './CardContextMenu';
import { useSortableHandle } from './SortableFicheLayer';

interface CardActionsProps {
  sectionId: string;
  editHandlers: FicheEditHandlers;
}

const COLOR_DOTS: { color: FicheColor; hex: string }[] = [
  { color: 'blue', hex: '#2e6cb5' },
  { color: 'green', hex: '#3e9a5e' },
  { color: 'purple', hex: '#8a4dbf' },
  { color: 'orange', hex: '#e3823a' },
  { color: 'red', hex: '#d34a4a' },
  { color: 'yellow', hex: '#f5d24c' },
];

/**
 * Floating action bar anchored to the top-right corner of a card while in
 * edit mode. Hover-only by default; opens a context menu for advanced actions.
 *
 * The hover affordance lives in the parent CardWrapper via `group` + `group-hover`.
 */
export function CardActions({ sectionId, editHandlers }: CardActionsProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const sortable = useSortableHandle();

  return (
    <>
      <div
        className="absolute top-[-12px] right-[-8px] flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
        style={{ zIndex: 4 }}
      >
        {/* Drag handle — only present when a sortable context is in scope */}
        {sortable ? (
          <button
            type="button"
            aria-label="Déplacer cette carte"
            title="Glisse pour déplacer"
            className="w-7 h-7 grid place-items-center bg-white border-2 border-ink rounded-full text-ink hover:bg-accent-blue/10 shadow-ink-sm text-[14px] cursor-grab active:cursor-grabbing"
            data-testid="drag-handle"
            {...sortable.attributes}
            {...sortable.listeners}
          >
            ⠿
          </button>
        ) : null}
        <button
          type="button"
          aria-label="Monter cette carte"
          onClick={() => editHandlers.moveSection?.(sectionId, 'up')}
          className="w-7 h-7 grid place-items-center bg-white border-2 border-ink rounded-full text-ink hover:bg-accent-blue/10 shadow-ink-sm text-[14px]"
        >
          ↑
        </button>
        <button
          type="button"
          aria-label="Descendre cette carte"
          onClick={() => editHandlers.moveSection?.(sectionId, 'down')}
          className="w-7 h-7 grid place-items-center bg-white border-2 border-ink rounded-full text-ink hover:bg-accent-blue/10 shadow-ink-sm text-[14px]"
        >
          ↓
        </button>
        <button
          type="button"
          aria-label="Plus d'actions"
          onClick={(e) => {
            const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
            setMenuPos({ x: r.left + window.scrollX, y: r.bottom + 4 + window.scrollY });
            setMenuOpen(true);
          }}
          className="w-7 h-7 grid place-items-center bg-white border-2 border-ink rounded-full text-ink hover:bg-accent-blue/10 shadow-ink-sm text-[14px]"
        >
          ⋯
        </button>
      </div>
      {menuOpen && menuPos ? (
        <CardContextMenu
          x={menuPos.x}
          y={menuPos.y}
          onClose={() => setMenuOpen(false)}
          onDuplicate={() => editHandlers.duplicateSection?.(sectionId)}
          onDelete={() => editHandlers.removeSection?.(sectionId)}
          onRecolor={(c) => editHandlers.recolorSection?.(sectionId, c)}
          colors={COLOR_DOTS}
        />
      ) : null}
    </>
  );
}
